/**
 * Deterministischer Test-Bot (SCOPE_STAGE2 Pflicht-Tests): simuliert alle
 * Spieler-Entscheidungen regelbasiert aus dem RunState — kein eigener Zufall.
 * Wird vom Voller-Run-Determinismus-Test und Smoke-Tools genutzt.
 */
import type { BalanceConfig } from '../config/balance'
import type { FocusId, RunState } from './types'
import { continueAfterResult, newRun, pickFocus, startFight } from './runState'
import { buySlot, choosePath, reroll, takeBonusChamp } from './shop'
import { deployCap, repairCost } from './economy'
import {
  benchChampions, boardChampions, equipWeapon, fractureAt, placeChampion, storageWeapons,
} from './roster'
import { executeMerge, findMerges } from './merge'
import { repairFracture } from './fractures'
import type { HexPos } from '../sim/hex'

const FOCUS_CYCLE: FocusId[] = ['gold', 'merge', 'variety', 'rarityChamps', 'rarityItems']

function freeOwnHex(state: RunState): HexPos | null {
  const g = state.config.grid
  for (let row = g.rowsPerSide; row < g.rowsPerSide * 2; row++) {
    for (let col = 0; col < g.cols; col++) {
      const hex = { col, row }
      if (fractureAt(state, hex)) continue
      if (state.champions.some((c) => c.hex && c.hex.col === col && c.hex.row === row)) continue
      return hex
    }
  }
  return null
}

/** eine Shop-Phase (oder Fokus-Pick) komplett spielen */
export function botPlayShopPhase(state: RunState): void {
  if (state.phase === 'focusPick') {
    const idx = Math.floor(state.round / state.config.progression.recurringPveInterval) % FOCUS_CYCLE.length
    pickFocus(state, FOCUS_CYCLE[idx]!)
  }
  if (state.phase !== 'shop') return
  if (state.shop.path === null) {
    const wantItem = state.round % 2 === 0
    if (!wantItem || !choosePath(state, 'item')) {
      choosePath(state, 'character') // S2-Guard-Fallback (0 Champions -> Character-Pfad)
    }
  }
  // S1: Gratis-Bonus-Charakter nehmen (wenn Item-Pfad + Bench-Platz)
  takeBonusChamp(state)
  // Free Rerolls verbrauchen (mischt die Auswahl, kostet nichts)
  while (state.shop.freeRerolls > 0) reroll(state, 'board')
  // kaufen: links nach rechts, solange Gold reicht (buySlot validiert Bench/Storage)
  for (let i = 0; i < state.shop.slots.length; i++) buySlot(state, i)
  // sicherstellen dass mindestens 1 Unit kaempfen kann: notfalls rerollen bis ein
  // Champion bezahlbar ist (deterministisch; bricht ab wenn Gold nicht mehr reicht)
  let guard = 0
  while (boardChampions(state).length === 0 && benchChampions(state).length === 0 && guard++ < 20) {
    if (state.shop.path !== 'character') break
    if (!reroll(state, 'board')) break
    for (let i = 0; i < state.shop.slots.length; i++) buySlot(state, i)
  }
  // Merges ausfuehren (solange moeglich)
  let merges = findMerges(state)
  let mergeGuard = 0
  while (merges.length > 0 && mergeGuard++ < 20) {
    executeMerge(state, merges[0]!)
    merges = findMerges(state)
  }
  // aufstellen bis Cap
  const cap = deployCap(state.round, state.config)
  for (const c of [...benchChampions(state)].sort((a, b) => a.uid - b.uid)) {
    if (boardChampions(state).length >= cap) break
    const hex = freeOwnHex(state)
    if (!hex) break
    placeChampion(state, c.uid, hex)
  }
  // ausruesten: Board-Units ohne Waffe bekommen die erste passende Storage-Waffe
  for (const c of boardChampions(state).sort((a, b) => a.uid - b.uid)) {
    if (c.weaponUids.length > 0) continue
    for (const w of [...storageWeapons(state)].sort((a, b) => a.uid - b.uid)) {
      if (equipWeapon(state, c.uid, w.uid)) break
    }
  }
  // Reparatur wenn locker leistbar
  if (state.fractures.length > 0 && state.gold >= repairCost(state.round, state.config) + 10) {
    repairFracture(state)
  }
}

export interface BotRunResult {
  state: RunState
  rounds: number
  aborted: boolean
}

/** kompletten Run spielen (bis 0 HP oder Runden-Limit) */
export function botPlayRun(masterSeed: number, config: BalanceConfig, maxRounds = 80): BotRunResult {
  const state = newRun(masterSeed, config)
  let aborted = false
  let guard = 0
  while (state.phase !== 'ended' && guard++ < maxRounds * 4) {
    if (state.round > maxRounds) {
      aborted = true
      break
    }
    botPlayShopPhase(state)
    const fight = startFight(state)
    if (!fight) {
      // kein kampffaehiges Board (extrem seltener Shop-Pech-Fall) — Run abbrechen
      aborted = true
      break
    }
    continueAfterResult(state)
  }
  return { state, rounds: state.round, aborted }
}
